/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { camelCase } from 'lodash-es';

import template from './header.template';
import { HeaderConfig, HeaderLink, CBWSlidedownElement } from './types';
import fontsStyle from '../../../../core/src/scss/fonts.scss?inline';
import headerStyle from './header.scss?inline';
import stringReplacer from '../../../../core/src/ts/stringReplacer';
import { defaultHeaderLinks } from './defaultHeaderLinks';
import { formatLinks, getCategory, addQueryParam } from './links';
import { defaultLoginLinks } from './defaultLoginLinks';
import { LoginLink } from '../Login/types';
import { addComponentTrackingCode, fireComponentLoaded } from '../../../../core/src/ts/analytics/analytics';
import { shadowLinkCloner } from '../../../../core/src/ts/analytics/shadowLinkCloner';
import { apricotVersion } from '../../../../core/src/ts/variables/variables';

class Header extends HTMLElement {
    constructor() {
        super();

        // Add a shadow DOM
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const shadowDOM = this.attachShadow({ mode: 'open' });
    }
    getWindowLocationHref = (): string => {
        return /iframe/.test(window.location.href) ? window.parent.location.href : window.location.href;
    };

    connectedCallback() {
        const shadowDOM = this.shadowRoot;
        if (!shadowDOM) {
            return;
        }

        const defaultData: HeaderConfig = {
            appId: 466,
            skipTargetId: 'main_content',
            programType: 'corporate',
            useIconLogo: true,
            useGlobalNavigation: true,
            useLoginWidget: true,
            useCbLogoLink: true,
            useSearchWidget: true,
            destinationUrl: '',
            loggedInLinksUrl: '',
            idp: '',
            siteTitle: '',
            searchType: '',
            homeLink: 'https://www.collegeboard.org',
            homeLinkLocation: 'The College Board',
            skipEnroll: '',
            destinationUrlLogout: '',
        };

        const linksURL = 'https://athena.collegeboard.org/api/header-links.json';

        const warnRequired = (param: string, message: string): void => {
            console.warn(`[cb-header] ${param} is required. ${message}`);
        };

        /**
         * Open/close the slidedown and switch the active panel wrapper.
         * @param className wrapper to make active (e.g. 'cbw-slidedown-nav')
         */
        const slide = (className: string) => {
            const slidedown = shadowDOM.querySelector('cbw-slidedown') as CBWSlidedownElement | null;
            if (!slidedown) return;

            const currentPanel = slidedown.getAttribute('active-class');
            const samePanel = currentPanel === className;

            // If it is already open we close first …
            if (slidedown.state === 'open') {
                slidedown.close();

                // … then (optionally) reopen a different wrapper.
                if (!samePanel) {
                    slidedown.setAttribute('active-class', className);
                    slidedown.open();
                }
            } else {
                // If closed, ensure the requested wrapper is active and open it.
                if (!samePanel) {
                    slidedown.setAttribute('active-class', className);
                }
                slidedown.open();
            }
        };

        const stringParams = [
            'skipTargetId',
            'programType',
            'siteTitle',
            'destinationUrl',
            'loggedInLinksUrl',
            'idp',
            'searchType',
            'homeLink',
            'homeLinkLocation',
            'skipEnroll',
            'destinationUrlLogout',
        ];
        const numberParams = ['appId'];
        const boolParams = ['useGlobalNavigation', 'useLoginWidget', 'useCbLogoLink', 'useSearchWidget', 'useIconLogo'];
        const attributes = this.getAttributeNames();
        const getValidUrl = (url: string): string => {
            try {
                // This will only work if the URL is valid. Otherwise, a type error is thrown.
                new URL(url);
                return url;
            } catch (err) {
                return 'https://www.collegeboard.org';
            }
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any = {};
        attributes.forEach(attribute => {
            const param = camelCase(attribute);
            if (stringParams.includes(param)) {
                data[param] = this.getAttribute(attribute);
            } else if (numberParams.includes(param)) {
                data[param] = this.getAttribute(attribute);
            } else if (boolParams.includes(param)) {
                data[param] = this.getAttribute(attribute) === 'true';
            }
        });
        data.homeLink = getValidUrl(data.homeLink);
        if (!data.skipTargetId) {
            warnRequired('skipTargetId', 'Please add this REQUIRED attribute for accessibility.');
        }

        data = {
            ...defaultData,
            ...data,
        };

        // corporate combination (no title) is always useIconLogo
        // corporate combination (with title) is always NOT useIconLogo
        if (!data.siteTitle) {
            data.useIconLogo = true;
        } else if (data.siteTitle && data.programType.toLowerCase() == 'corporate') {
            data.useIconLogo = false;
        }

        const renderLinks = (links: HeaderLink[]) => {
            const linkDiv: HTMLElement | null = shadowDOM.querySelector('.cbw-slidedown-nav-body');
            if (linkDiv && data.showLinks === false) {
                linkDiv.innerHTML = '';
            } else {
                if (linkDiv) {
                    linkDiv.innerHTML = '';
                    // Render the links.
                    const items = formatLinks(links, 'gh');

                    const catAssessments = getCategory(
                        items.filter((link: HeaderLink) => link.category === 'K-12'),
                        true,
                        'cbw-border-top-4-blue2',
                        'Assessments',
                    );
                    linkDiv.append(catAssessments);

                    const clearFixDiv = document.createElement('div');
                    clearFixDiv.classList.add('cbw-clearfix', 'visible-sm-block');
                    linkDiv.append(clearFixDiv);

                    const catAccess = getCategory(
                        items.filter((link: HeaderLink) => link.category === 'Access'),
                        false,
                        'cbw-border-top-4-blue5',
                        'Access',
                    );
                    linkDiv.append(catAccess);

                    const catHigherEd = getCategory(
                        items.filter((link: HeaderLink) => link.category === 'Higher Ed'),
                        true,
                        'cbw-border-top-4-purple1',
                        'Higher Education',
                    );
                    linkDiv.append(catHigherEd);

                    const catOrganization = getCategory(
                        items.filter((link: HeaderLink) => link.category === 'Organization' && link.type !== 'mobile'),
                        false,
                        'cbw-border-top-4-white',
                        'Organization',
                    );
                    linkDiv.append(catOrganization);
                }
            }
            // Add hover style
            const logosItems = linkDiv?.querySelectorAll('.cbw-slidedown-nav-body .cbw-logo');
            logosItems?.forEach(logoItem => {
                const parentLI = logoItem.parentElement as HTMLElement;
                logoItem?.addEventListener('mouseover', () => {
                    parentLI.classList.add('hovering');
                });
                logoItem?.addEventListener('mouseout', () => {
                    parentLI.classList.remove('hovering');
                });
            });
        };

        let programBgColor = 'white';
        let programBgXsColor = 'white';
        let programBorderColor = 'black1';
        let fontXsColor;
        if (data.siteTitle && data.programType.toLowerCase() === 'corporate') {
            fontXsColor = 'black1';
        } else if (data.useIconLogo) {
            fontXsColor = 'white';
        } else {
            fontXsColor = 'black1';
        }

        let programFontColor =
            data.programType.toLowerCase() == 'corporate' ? 'black' : data.useIconLogo ? 'white' : 'black';
        switch (data.programType.toLowerCase()) {
            case 'k-12':
                programBgColor = data.useIconLogo ? 'blue2' : 'white';
                programBgXsColor = data.useIconLogo ? 'blue2' : 'white';
                programBorderColor = 'blue2';
                break;
            case 'access':
            case 'big-future':
                programBgColor = data.useIconLogo ? 'blue5' : 'white';
                programBgXsColor = data.useIconLogo ? 'blue5' : 'white';
                programBorderColor = 'blue5';
                break;
            case 'higher-ed':
                programBgColor = data.useIconLogo ? 'purple1' : 'white';
                programBgXsColor = data.useIconLogo ? 'purple1' : 'white';
                programBorderColor = 'purple1';
                break;
            case 'corporate':
                if (data.siteTitle) {
                    programBgColor = 'white';
                    programFontColor = 'black';
                    programBgXsColor = 'white';
                } else {
                    programBgColor = 'black1';
                    programFontColor = 'white';
                    programBgXsColor = 'black1';
                }
                break;
            default:
                programBgColor = 'white';
        }

        const renderMobileSearch = () => {
            const mobileSearchDiv = shadowDOM.querySelector('.cbw-mobile-search-body');
            const mobileSearchComponent = document.createElement('cbw-search');
            mobileSearchComponent.setAttribute('app-id', data.appId);
            mobileSearchComponent.setAttribute('search-type', data.searchType);
            mobileSearchDiv?.appendChild(mobileSearchComponent);

            const mobileTrigger = shadowDOM.querySelector('.cbw-open-mobile-search');
            mobileTrigger?.addEventListener('click', event => {
                event.preventDefault();
                slide('cbw-slidedown-search');
            });
            const mobileClose = shadowDOM.querySelector('.cbw-close-mobile-search');
            mobileClose?.addEventListener('click', event => {
                event.preventDefault();
                slide('cbw-slidedown-search');
            });
        };

        const fetchNavLinks = async () => {
            try {
                fetch(linksURL)
                    .then(res => {
                        res.json()
                            .then(data => {
                                renderLinks(data);
                            })
                            .catch(error => {
                                console.error(error);
                                renderLinks(defaultHeaderLinks);
                            });
                    })
                    .catch(error => {
                        console.error(`Nav links retrieval error: ${error}`);
                        renderLinks(defaultHeaderLinks);
                    });
            } catch (errors) {
                console.error(errors);
                renderLinks(defaultHeaderLinks);
            }
        };

        this.addEventListener('slidedown_connected', () => {
            if (data.useGlobalNavigation) {
                fetchNavLinks();
            }
            if (data.useSearchWidget) {
                renderMobileSearch();
            }
        });

        const acornLogoClass = 'cbw-acorn';
        const fullCbLogoClass = 'cbw-cb';

        const globalNavTriggerIsAcorn = (): boolean => {
            // If the program is corporate and we have no title, show full logo.
            // For other programs acorn can be mobile trigger.
            return (
                data.programType.toLowerCase() !== 'corporate' ||
                (data.programType.toLowerCase() === 'corporate' && data.siteTitle)
            );
        };
        const mainLogoDesktopIsAcorn = (): boolean => {
            // If the site title is empty, we use the full CB logo.
            // Otherwise, we use the acorn logo.
            return !!data.siteTitle;
        };

        const mainLogoMobileIsAcorn = (): boolean => {
            // If there is global navigation, the mobile logo becomes the full
            // CB logo, centered, toggled to only open when the slidedown is open.
            if (data.useGlobalNavigation) {
                return false;
            }
            // When there is no global navigation:
            // If the program is corporate, and no title, full logo.
            if (data.programType.toLowerCase() === 'corporate' && !data.siteTitle) {
                return false;
            }

            // Otherwise, use acorn only if there is a site title.
            return !!data.siteTitle;
        };

        const mainLogoMobileIsCentered = (): boolean => {
            // If global navigation is used, the main logo is centered. Otherwise,
            // it should be left aligned.
            return data.useGlobalNavigation;
        };

        const mainLogoMobileIsInitiallyHidden = (): boolean => {
            // If global navigation is used, the main logo is initially hidden. It then
            // gets toggled to visible only when the slidedown is open. Otherwise,
            // it should be shown initially and is not subject to toggling.
            return data.useGlobalNavigation;
        };

        const replacements: { [key: string]: string } = {
            headerStyle: headerStyle.toString(),
            useIconLogo: data.useIconLogo ? 'cbw-use-logo' : '',
            programBgColor: programBgColor,
            programBgXsColor: programBgXsColor,
            fontXsColor: fontXsColor,
            programBorderColor: programBorderColor,
            programFontColor: programFontColor,
            mainLogoTag: data.useCbLogoLink ? 'a' : 'span',
            mainLogoHref: data.useCbLogoLink ? `href="${data.homeLink}"` : '',
            mainLogoText: data.homeLinkLocation,
            logoLabel: data.siteTitle,
            cbLogoClass: mainLogoDesktopIsAcorn() ? acornLogoClass : fullCbLogoClass,
            cbMobileTriggerClass: globalNavTriggerIsAcorn() ? acornLogoClass : fullCbLogoClass,
            mainLogoMobileClass: mainLogoMobileIsAcorn() ? acornLogoClass : fullCbLogoClass,
            mainLogoMobileCenteredClass: mainLogoMobileIsCentered() ? 'cbw-main-logo-center-xs' : '',
            mainLogoMobileHideClass: mainLogoMobileIsInitiallyHidden() ? 'cbw-hidden-xs-up' : '',
            logoLabelClasses: data.useIconLogo ? 'sr-only' : '',
            iconLogoClasses: data.useIconLogo
                ? 'cbw-logo cbw-' + data.siteTitle.toLowerCase().replace(' ', '-')
                : 'cbw-logo-text',
            skipTargetId: data.skipTargetId,
            appId: data.appId,
            searchType: data.searchType,
        };

        shadowDOM.innerHTML = stringReplacer(template, replacements);
        // Add analytics tracking code to the light Dom web component element.
        addComponentTrackingCode('cbw-header', 'header');
        fireComponentLoaded('header');

        // Remove login widget if not used
        if (data.useLoginWidget) {
            const renderLoginGreeting = (firstName: string) => {
                const loginGreetingDiv: HTMLElement | null = shadowDOM.querySelector('.cbw-greetings-wrapper');
                if (loginGreetingDiv) {
                    loginGreetingDiv.innerHTML = '';
                    const heading = document.createElement('h2');
                    const greetingPreface = document.createElement('span');
                    greetingPreface.classList.add('cbw-greeting-preface');
                    greetingPreface.innerHTML = 'Hi, ';
                    heading.append(greetingPreface);
                    const greetingName = document.createElement('span');
                    greetingName.classList.add('cbw-greeting-name');
                    greetingName.innerHTML = firstName + '!';
                    heading.append(greetingName);
                    loginGreetingDiv.append(heading);
                }
            };
            const renderSignOut = () => {
                // First check destinationUrlLogout , then check destinationUrl if neither was set fall back to current window location (CMS-10435).
                const logoutDestinationURL = data.destinationUrlLogout
                    ? data.destinationUrlLogout
                    : data.destinationUrl
                      ? data.destinationUrl
                      : this.getWindowLocationHref();
                const signOutForm = `
                <form accept-charset="utf-8" method="get" action="https://account.collegeboard.org/login/logout">
                    <input type="hidden" name="userType" value="S">
                    <input type="hidden" name="DURL" value="${logoutDestinationURL}">
                    <input type="hidden" name="appId" value="${data.appId}">
                    <input type="hidden" name="formState" value="1">
                    <button type="submit" class="cbw-btn" data-cbtrack-linktype="toggle" data-cbtrack-label="Sign Out">Sign Out</button>
                </form>
                `;
                const signOutDiv: HTMLElement | null = shadowDOM.querySelector('.cbw-signout-wrapper');
                if (signOutDiv) {
                    signOutDiv.innerHTML = signOutForm;
                    const signOutFormElement = signOutDiv.querySelector('form[method="post"]') as HTMLFormElement;
                    signOutFormElement?.addEventListener('submit', event => {
                        event.preventDefault();
                        const durlInput = signOutFormElement.querySelector('input[name="DURL"]');
                        if (durlInput && durlInput.getAttribute('value') === '') {
                            durlInput.setAttribute('value', logoutDestinationURL);
                        }
                        signOutFormElement.submit();
                    });
                }
            };
            const renderLoginLinks = (loginLinks: LoginLink[], role: 'student' | 'parent' | 'professional') => {
                const linkDiv: HTMLElement | null = shadowDOM?.querySelector('.cbw-loggedin-links-nav');
                if (linkDiv) {
                    linkDiv.innerHTML = '';
                    const listElement = document.createElement('ul');
                    loginLinks
                        .filter(link => link.type === role)
                        .forEach((link: LoginLink) => {
                            const listItem = document.createElement('li');
                            const adjustedUrl = addQueryParam(link.url, 'navId', `${link.linkCode}`);
                            const anchor = document.createElement('a');
                            anchor.setAttribute('href', adjustedUrl);
                            anchor.setAttribute('data-cbtrack-linktype', 'nav');
                            anchor.setAttribute('data-cbtrack-label', link.name);
                            anchor.setAttribute('tabindex', '-1');
                            const anchorText = document.createElement('span');
                            anchorText.innerHTML = link.name;
                            anchor.appendChild(anchorText);
                            const anchorIcon = document.createElement('span');
                            anchorIcon.classList.add('cbw-icon', 'cbw-right');
                            anchorIcon.setAttribute('aria-hidden', 'true');
                            anchor.appendChild(anchorIcon);
                            listItem.appendChild(anchor);
                            listElement.appendChild(listItem);
                        });
                    linkDiv.appendChild(listElement);
                }
            };
            // Prefetch login links
            const fetchAndRenderLoginLinks = (role: 'student' | 'parent' | 'professional') => {
                const loginURL = data.loggedInLinksUrl
                    ? data.loggedInLinksUrl
                    : 'https://athena.collegeboard.org/api/identity-links.json';
                try {
                    fetch(loginURL)
                        .then(res => {
                            res.json()
                                .then(data => {
                                    renderLoginLinks(data, role);
                                })
                                .catch(error => {
                                    console.error(error);
                                    renderLoginLinks(defaultLoginLinks, role);
                                });
                        })
                        .catch(error => {
                            console.error(`Notification retrieval error: ${error}`);
                            renderLoginLinks(defaultLoginLinks, role);
                        });
                } catch (errors) {
                    console.error(errors);
                    renderLoginLinks(defaultLoginLinks, role);
                }
            };

            // Listen for login/logout and add/remove slide click event
            const clickToSlideLogin = () => {
                slide('cbw-slidedown-login');
            };

            this.addEventListener('loginEvent', ((event: CustomEvent) => {
                renderLoginGreeting(event.detail.firstName);
                renderSignOut();
                fetchAndRenderLoginLinks(event.detail.role);
            }) as EventListener);
            this.addEventListener('loginButtonPress', (() => {
                clickToSlideLogin();
            }) as EventListener);
            const loginClose = shadowDOM.querySelector('.cbw-close-login');
            loginClose?.addEventListener('click', event => {
                event.preventDefault();
                slide('cbw-slidedown-login');
            });
            const loginWidgetContainer = shadowDOM.querySelector('.cbw-login-container');
            const loginComponent = document.createElement('cbw-login');
            loginComponent.setAttribute('idp', data.idp);
            loginComponent.setAttribute('destination-url', data.destinationUrl);
            loginComponent.setAttribute('app-id', data.appId);
            loginComponent.setAttribute('skip-enroll', data.skipEnroll);
            loginWidgetContainer?.appendChild(loginComponent);
        }

        // Remove search widget if not used.
        if (data.useSearchWidget === false) {
            const searchWidgets = shadowDOM.querySelectorAll('.cbw-search-wrapper');
            searchWidgets.forEach(searchWidget => {
                searchWidget.remove();
            });
            this.querySelector('.cbw-search-dialog')?.remove();
        }

        // Render or remove global nav.
        const globalNavLink = shadowDOM.querySelector('.cbw-header-panel-trigger');
        if (data.useGlobalNavigation === false) {
            globalNavLink?.remove();
        } else {
            shadowDOM.querySelector('cbw-slidedown')?.setAttribute('cb-link', data.useCbLogoLink);

            globalNavLink?.addEventListener('click', event => {
                event.preventDefault();
                slide('cbw-slidedown-nav');
            });
        }

        const localLogoDiv = shadowDOM.querySelector('h2.cbw-local-logo');
        // If there is no title or
        // remove the local logo div
        if (!data.siteTitle) {
            localLogoDiv?.remove();
        }

        // if corporate is selected
        // and no title is passed
        // remove the local logo div for xs
        if (data.programType.toLowerCase() === 'corporate' && !data.siteTitle) {
            localLogoDiv?.classList.add('cbw-hidden-permanent-xs');
        }

        // The dimmer is only visible when the slidedown is open. clicking should slide it closed.
        const dimmer = shadowDOM.querySelector('.cbw-dimmer');
        dimmer?.addEventListener('click', event => {
            event.preventDefault();
            const currentlyActive = this.shadowRoot?.querySelector('cbw-slidedown')?.getAttribute('active-class');
            slide(currentlyActive ?? 'cbw-slidedown-nav');
        });

        // Adjust mobile header when slidedown triggered.
        this.addEventListener('slideDownEvent', ((event: CustomEvent) => {
            const isOpen = event.detail.isOpen;
            const activeClass = event.detail.activeClass;

            const toggleClass = (selector: string, className: string, whenToAdd: 'open' | 'closed'): void => {
                const element = this?.shadowRoot?.querySelector(selector);
                if (element) {
                    const newState = isOpen ? 'open' : 'closed';
                    if (newState === whenToAdd) {
                        element.classList.add(className);
                    } else {
                        element.classList.remove(className);
                    }
                }
            };
            if (activeClass === 'cbw-slidedown-nav') {
                toggleClass('#globalHeader', 'cbw-bg-black-xs', 'open');
                toggleClass('.cbw-close-navigation', 'cbw-hidden-xs-up', 'closed');
                toggleClass('h1.cbw-main-logo-mobile', 'cbw-hidden-xs-up', 'closed');
                toggleClass('.cbw-dimmer', 'cbw-hidden-xs-up', 'closed');
                toggleClass('h2.cbw-local-logo', 'cbw-hidden-xs', 'open');
                toggleClass('.cbw-header-container-login', 'cbw-hidden-xs', 'open');

                const openNavs = this.querySelectorAll('.cbw-open-navigation');
                if (isOpen) {
                    openNavs?.forEach(el => el.classList.add('cbw-hidden-xs-up'));
                } else {
                    openNavs?.forEach(el => el.classList.remove('cbw-hidden-xs-up'));
                }
            }
            if (activeClass === 'cbw-slidedown-search') {
                toggleClass('.cbw-dimmer', 'cbw-hidden-xs-up', 'closed');
                toggleClass('.cbw-close-mobile-search', 'cbw-hidden-xs-up', 'closed');
                toggleClass('.cbw-search-wrapper', 'cbw-hidden-xs', 'open');
                toggleClass('cbw-login', 'cbw-hidden-xs', 'open');
            }
            if (activeClass === 'cbw-slidedown-login') {
                toggleClass('.cbw-dimmer', 'cbw-hidden-xs-up', 'closed');
                toggleClass('.cbw-close-login', 'cbw-hidden-xs-up', 'closed');
                toggleClass('.cbw-search-wrapper', 'cbw-hidden-xs-up', 'open');
                toggleClass('cbw-login', 'cbw-hidden-xs-up', 'open');
            }

            // Handle trigger accessibility.
            let triggerLink;
            switch (activeClass) {
                case 'cbw-slidedown-nav':
                    triggerLink = shadowDOM.querySelector('.cbw-header-panel-trigger-link');
                    break;
                case 'cbw-slidedown-search':
                    triggerLink = shadowDOM.querySelector('.cbw-open-mobile-search');
                    break;
                case 'cbw-slidedown-login':
                    triggerLink = shadowDOM.querySelector('.cbw-header-login');
                    break;
                default:
                    break;
            }
            if (isOpen) {
                triggerLink?.setAttribute('aria-expanded', 'true');
            } else {
                triggerLink?.setAttribute('aria-expanded', 'false');
                (triggerLink as HTMLElement)?.focus();
            }
        }) as EventListener);

        // GS-10856
        // keep tab navigation in place
        if (data.useCbLogoLink) {
            const closeButton = shadowDOM.querySelector('.cbw-header-panel-trigger-link');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            closeButton?.addEventListener('keydown', (event: any) => {
                const isOpen = closeButton?.getAttribute('aria-expanded');
                if (window.innerWidth <= 768 && isOpen === 'true') {
                    // forward or backward
                    if ((event.key === 'Tab' && event.shiftKey) || (event.key === 'Tab' && !event.shiftKey)) {
                        event.preventDefault();
                        const cbLink = shadowDOM.querySelector('.cbw-main-logo-mobile a.cbw-cb');
                        (cbLink as HTMLElement)?.focus();
                    }
                }
            });
        }

        const stylesheet = document.createElement('style');
        stylesheet.textContent = fontsStyle.toString();
        stylesheet.textContent += `.cbw-body-lock { position: fixed; width: 100%;}`;
        this.appendChild(stylesheet);

        const linkElem = document.createElement('link');
        linkElem.setAttribute('rel', 'stylesheet');
        linkElem.setAttribute('href', `//atlas.collegeboard.org/apricot/prod/${apricotVersion}/widgets.css`);
        shadowDOM.appendChild(linkElem);

        shadowLinkCloner(this);
    }
    static get observedAttributes() {
        return ['destination-url'];
    }
    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        switch (name) {
            case 'destination-url':
                // Find the cbw-login component and update the destination-url attribute.
                const loginComponent = this.shadowRoot?.querySelector('cbw-login');
                if (loginComponent) {
                    loginComponent.setAttribute('destination-url', newValue);
                }
                // If the user is logged in, adjust the sign out form.
                const signOutDiv: HTMLElement | null | undefined =
                    this.shadowRoot?.querySelector('.cbw-signout-wrapper');
                if (signOutDiv) {
                    const durlInput = signOutDiv.querySelector('input[name="DURL"]');
                    if (durlInput) {
                        durlInput.setAttribute('value', newValue);
                    }
                }
                break;
            default:
                break;
        }
    }
}

export default Header;
